import { useCallback, useState, useRef, startTransition } from 'react';
import { InitialData, IconCollectionState } from 'src/types';
import { INITIAL_DATA, INITIAL_SCENE_STATE } from 'src/config';
import {
  getFitToViewParams,
  CoordsUtils,
  categoriseIcons,
  generateId,
  getItemByIdOrThrow
} from 'src/utils';
import * as reducers from 'src/stores/reducers';
import { useModelStore } from 'src/stores/modelStore';
import { useSceneStore } from 'src/stores/sceneStore';
import { useView } from 'src/hooks/useView';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { modelSchema } from 'src/schemas/model';
import { groupManager } from '@/features/groups/GroupManager';
import { groupInstanceManager } from '@/features/groups/GroupInstanceManager';
import { toast } from '@/stores/toastStore';

/**
 * Migrates icon paths from old reshot structure to new custom structure
 * Old: /icons/reshot/storage/cloud.svg
 * New: /icons/custom/storage/cloud.svg
 */
const migrateIconPaths = (data: InitialData): InitialData => {
  // Deep clone to avoid mutating original data
  const migrated = JSON.parse(JSON.stringify(data));
  let migratedCount = 0;

  // Migrate icons array
  if (migrated.icons && Array.isArray(migrated.icons)) {
    migrated.icons = migrated.icons.map((icon: any) => {
      if (icon.url && typeof icon.url === 'string' && icon.url.includes('/icons/reshot/')) {
        migratedCount++;
        return {
          ...icon,
          url: icon.url.replace('/icons/reshot/', '/icons/custom/')
        };
      }
      return icon;
    });
  }

  if (migratedCount > 0) {
    console.log(`🔄 Migrated ${migratedCount} icon path(s) from /icons/reshot/ to /icons/custom/`);
  }

  return migrated;
};

export const useInitialDataManager = () => {
  const [isReady, setIsReady] = useState(false);
  const prevInitialData = useRef<InitialData>();
  const model = useModelStore((state) => {
    return state;
  });
  const sceneActions = useSceneStore((state) => state.actions);
  const uiStateActions = useUiStateStore((state) => {
    return state.actions;
  });
  const rendererEl = useUiStateStore((state) => {
    return state.rendererEl;
  });
  const { changeView } = useView();

  const load = useCallback(
    (_initialData: InitialData) => {
      if (!_initialData || prevInitialData.current === _initialData) return;

      setIsReady(false);

      // Migrate icon paths from old reshot structure to new custom structure
      const migratedData = migrateIconPaths(_initialData);

      const validationResult = modelSchema.safeParse(migratedData);

      if (!validationResult.success) {
        // Format validation errors for display
        const errors = validationResult.error.errors;
        const errorCount = errors.length;
        const firstErrors = errors.slice(0, 3); // Show first 3 errors

        // Create a detailed error message
        const errorSummary = firstErrors
          .map(err => {
            const path = err.path.join('.');
            return `  • ${path}: ${err.message}`;
          })
          .join('\n');

        const moreErrors = errorCount > 3 ? `\n  ... and ${errorCount - 3} more errors` : '';

        const errorMessage =
          `Failed to load diagram due to ${errorCount} validation error${errorCount > 1 ? 's' : ''}:\n\n` +
          errorSummary +
          moreErrors +
          `\n\nPlease check the browser console (F12) for full details.`;

        // Log full errors to console for debugging
        console.group('❌ Diagram Validation Errors');
        console.error('Validation failed with', errorCount, 'error(s):');
        errors.forEach((err, index) => {
          console.error(`${index + 1}.`, err.path.join('.'), '-', err.message);
        });
        console.groupEnd();

        toast.error(errorMessage, 8000);
        return;
      }

      const initialData = migratedData;

      if (initialData.views.length === 0) {
        const updates = reducers.view({
          action: 'CREATE_VIEW',
          payload: {},
          ctx: {
            state: { model: initialData, scene: INITIAL_SCENE_STATE },
            viewId: generateId()
          }
        });

        Object.assign(initialData, updates.model);
      }

      prevInitialData.current = initialData;

      // Batch all state updates in a single transition to prevent screen tearing
      // This reduces multiple re-renders and animation conflicts
      startTransition(() => {
        model.actions.set(initialData);
        // Loading is the history baseline, not a user editing operation.
        model.actions.clearHistory();
        sceneActions.clearHistory();

        // Load groups if present
        if (initialData.groups) {
          groupManager.loadGroups(initialData.groups);
        }

        // Load and auto-sync group instances if present
        if (initialData.groupInstances && initialData.groupInstances.length > 0) {
          groupInstanceManager.loadInstances(initialData.groupInstances);

          // Auto-sync instances with master groups on load
          // Check if auto-sync is enabled (default: true)
          const autosync = localStorage.getItem('flowi-autosync-groups') !== 'false';

          if (autosync) {
            // Sync in background, don't block diagram load
            // Note: This promise is intentionally not awaited to avoid blocking the transition
            groupInstanceManager.syncAllInstances().catch(error => {
              console.error('Error auto-syncing group instances:', error);
            });
          }
        }

        const view = getItemByIdOrThrow(
          initialData.views,
          initialData.view ?? initialData.views[0].id
        );

        changeView(view.value.id, initialData);

        if (initialData.fitToView) {
          const rendererSize = rendererEl?.getBoundingClientRect();

          const { zoom, scroll } = getFitToViewParams(view.value, {
            width: rendererSize?.width ?? 0,
            height: rendererSize?.height ?? 0
          });

          uiStateActions.setScroll({
            position: scroll,
            offset: CoordsUtils.zero()
          });

          uiStateActions.setZoom(zoom);
        }

        const categoriesState: IconCollectionState[] = categoriseIcons(
          initialData.icons
        ).map((collection) => {
          return {
            id: collection.name,
            isExpanded: false
          };
        });

        uiStateActions.setIconCategoriesState(categoriesState);

        setIsReady(true);
      });
    },
    [changeView, model.actions, rendererEl, sceneActions, uiStateActions]
  );

  const clear = useCallback(() => {
    load({ ...INITIAL_DATA, icons: model.icons, colors: model.colors });
    uiStateActions.resetUiState();
  }, [load, model.icons, model.colors, uiStateActions]);

  return {
    load,
    clear,
    isReady
  };
};
